/**
 * Curated cruise-traveler currency list — BR-FINANCE-003.
 *
 * PO directive 2026-02-14 (approved): keep the initial dropdown to a
 * curated set of currencies real cruise travelers actually encounter.
 * Full ISO 4217 (~160 codes) is deferred until we hear demand.
 *
 * The order below is intentional: home markets first, then major
 * European / Pacific, then the remainder alphabetically. The dropdown
 * renders them in this order so the most common choices are top of
 * the list.
 */
export const CURATED_CURRENCIES = [
  { code: "USD", label: "US Dollar",             symbol: "$"  },
  { code: "CAD", label: "Canadian Dollar",       symbol: "C$" },
  { code: "EUR", label: "Euro",                  symbol: "€"  },
  { code: "GBP", label: "British Pound",         symbol: "£"  },
  { code: "MXN", label: "Mexican Peso",          symbol: "Mex$" },
  { code: "JPY", label: "Japanese Yen",          symbol: "¥"  },
  { code: "AUD", label: "Australian Dollar",     symbol: "A$" },
  { code: "NZD", label: "New Zealand Dollar",    symbol: "NZ$" },
  { code: "CHF", label: "Swiss Franc",           symbol: "CHF" },
  { code: "NOK", label: "Norwegian Krone",       symbol: "kr" },
  { code: "SEK", label: "Swedish Krona",         symbol: "kr" },
  { code: "DKK", label: "Danish Krone",          symbol: "kr" },
  { code: "SGD", label: "Singapore Dollar",      symbol: "S$" },
  { code: "HKD", label: "Hong Kong Dollar",      symbol: "HK$" },
  { code: "AED", label: "UAE Dirham",            symbol: "AED" },
  { code: "THB", label: "Thai Baht",             symbol: "฿"  },
  { code: "BRL", label: "Brazilian Real",        symbol: "R$" },
  { code: "ZAR", label: "South African Rand",    symbol: "R"  },
];

export const CURATED_CURRENCY_CODES = CURATED_CURRENCIES.map((c) => c.code);

const BY_CODE = Object.fromEntries(CURATED_CURRENCIES.map((c) => [c.code, c]));

export function currencyMeta(code) {
  if (!code) return null;
  return BY_CODE[code.toUpperCase()] || null;
}

export function currencySymbol(code) {
  const m = currencyMeta(code);
  return m ? m.symbol : (code || "");
}

/**
 * ISO 4217 currencies whose minor unit is 0 decimal places.
 * JPY is the only one in the curated set. Adding a currency here
 * makes the formatter render whole units for that code.
 */
const ZERO_DECIMAL_CURRENCIES = new Set(["JPY"]);

/**
 * formatMoney — BR-FINANCE-009 · single source of truth for monetary
 * display across every Finance surface (Budget Register, Expenses
 * ledger, Budget vs Actual, Financial Documents, progress bars,
 * remaining balances, over-budget indicators, reports).
 *
 * Rules:
 *   • Always two fixed decimal places for standard-minor-unit
 *     currencies (USD, CAD, EUR, GBP, …). Never round to whole
 *     dollars regardless of magnitude — `$1,399.60` stays
 *     `$1,399.60`, never `$1,400`.
 *   • Zero-decimal ISO currencies (currently JPY) render whole
 *     units per ISO 4217.
 *   • Negative values render with a leading minus, then the currency
 *     symbol, then the magnitude — e.g. `-$50.00`.
 *   • Never performs currency conversion; presentation only.
 *
 * Calculations remain full-precision — only the display string is
 * shaped here.
 */
export function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const code = (currency || "USD").toUpperCase();
  const sym = currencySymbol(code);
  const n = Number(amount);
  const zeroDecimal = ZERO_DECIMAL_CURRENCIES.has(code);
  const digits = zeroDecimal ? 0 : 2;
  const magnitude = Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  const sign = n < 0 ? "-" : "";
  return `${sign}${sym}${magnitude}`;
}
